import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { LawpayPaymentService } from '../../../services/lawpay/lawpay-payment.service';
import { OnlinePaymentService } from '../../../services/online-payments/online-payment.service';

@Component({
  selector: 'app-online-payments-index',
  templateUrl: './online-payments-index.component.html',
  styleUrls: ['./online-payments-index.component.css']
})
export class OnlinePaymentsIndexComponent implements OnChanges, OnInit {
  @Input() matterId?: number;

  payments: any[] = [];
  selectedPayment: any = null;
  totalCount: number = 0;
  currentPage: number = 1;
  totalPages: number = 1;
  perPage: number = 25;
  loading: boolean = false;
  providers: string[] = [];
  statuses: string[] = [];
	  perPageOptions: number[] = [10, 25, 50, 100];
	  refundPayment: any = null;
	  refundReason: string = '';
	  refundAmount: number = 0;
	  refundPreview: any = null;
	  loadingPreview: boolean = false;
	  refunding: boolean = false;
	  showRefundModal: boolean = false;

  searchParams: any = {
    provider: '',
    status: '',
    from_date: '',
    to_date: '',
    per_page: 25,
    page: 1
  };

	  constructor(
	    private onlinePaymentService: OnlinePaymentService,
	    private lawpayPaymentService: LawpayPaymentService
	  ) {}

  ngOnInit(): void {
    this.applyMatterFilter();
    this.loadSearchOptions();
    this.search();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.matterId && !changes.matterId.firstChange) {
      this.applyMatterFilter();
      this.search(true);
    }
  }

  loadSearchOptions(): void {
    this.onlinePaymentService.getSearchOptions().then(result => {
      this.providers = result.providers || [];
      this.statuses = result.statuses || [];
      this.perPageOptions = result.per_page_options || this.perPageOptions;
    }).catch(error => {
      console.error('Error loading online payment filters:', error);
    });
  }

  search(resetPage: boolean = false): void {
    if (resetPage) {
      this.searchParams.page = 1;
    }

    this.loading = true;
    this.onlinePaymentService.index(this.compactParams(this.searchParams)).then(result => {
      this.payments = result.payments || [];
      this.totalCount = result.total_count || 0;
      this.currentPage = result.current_page || 1;
      this.totalPages = result.total_pages || 1;
      this.perPage = result.per_page || this.searchParams.per_page;
      this.selectedPayment = this.selectedPaymentStillVisible();
      this.loading = false;
    }).catch(error => {
      console.error('Error loading online payments:', error);
      this.loading = false;
    });
  }

  compactParams(params: any): any {
    const compacted: any = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        compacted[key] = params[key];
      }
    });
    return compacted;
  }

  selectedPaymentStillVisible(): any {
    if (!this.selectedPayment) return null;
    return this.payments.find(payment => {
      return payment.provider === this.selectedPayment.provider && payment.id === this.selectedPayment.id;
    }) || null;
  }

  pageChanged(event: any): void {
    this.searchParams.page = event.page;
    this.search();
  }

  setSelectedPayment(payment: any): void {
    this.selectedPayment = payment;
  }

  refresh(): void {
    this.search();
  }

  clearFilters(): void {
    this.searchParams = {
      provider: '',
      status: '',
      from_date: '',
      to_date: '',
      per_page: this.perPage,
      page: 1
    };
    this.applyMatterFilter();
    this.search();
  }

  applyMatterFilter(): void {
    const matterId = this.effectiveMatterId();
    if (matterId) {
      this.searchParams.matter_id = matterId;
    } else {
      delete this.searchParams.matter_id;
    }
  }

  effectiveMatterId(): number | null {
    if (this.matterId) return this.matterId;

    const match = window.location.pathname.match(/^\/matters\/(\d+)(?:\/|$)/);
    return match ? Number(match[1]) : null;
  }

  providerLabel(provider: string): string {
    if (provider === 'payload') return 'Payload';
    if (provider === 'lawpay') return 'LawPay';
    return provider || 'Unknown';
  }

  statusLabel(status: string): string {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  providerBadgeClass(provider: string): string {
    if (provider === 'payload') return 'badge-info';
    if (provider === 'lawpay') return 'badge-primary';
    return 'badge-secondary';
  }

  workflowBadgeClass(status: string): string {
    if (status === 'processed' || status === 'reconciled') return 'badge-success';
    if (status === 'unreconciled') return 'badge-warning';
    if (status === 'unprocessed') return 'badge-secondary';
    return 'badge-light';
  }

  providerStatusBadgeClass(status: string): string {
    if (status === 'processed' || status === 'completed' || status === 'paid') return 'badge-success';
    if (status === 'declined' || status === 'rejected' || status === 'failed') return 'badge-danger';
    if (status === 'pending') return 'badge-warning';
    return 'badge-secondary';
  }

  isRefundOrReversal(payment: any): boolean {
    if (!payment) return false;
    return !!(payment.refund_or_reversal || payment.is_reversal || payment.is_refunded);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  viewReceipt(payment: any): void {
    if (payment.receipt_id) {
      window.location.href = `/receipts/${payment.receipt_id}`;
    }
  }

  viewEntryGroup(payment: any): void {
    if (payment.entry_group_url) {
      window.location.href = payment.entry_group_url;
    } else if (payment.entry_group_id) {
      window.location.href = `/entry_groups/${payment.entry_group_id}`;
    }
  }

  viewInvoice(payment: any): void {
    if (payment.invoice_id) {
      window.location.href = `/invoices/${payment.invoice_id}`;
    }
  }

  viewMatter(payment: any): void {
    if (payment.matter_id) {
      window.location.href = `/matters/${payment.matter_id}`;
    }
  }

	  viewContact(payment: any): void {
	    if (payment.contact_id) {
	      window.location.href = `/contacts/${payment.contact_id}`;
	    }
	  }

	  canRefund(payment: any): boolean {
	    return payment && payment.refundable;
	  }

	  refundUnavailableText(payment: any): string {
	    if (!payment) return '';
	    if (payment.is_refunded) return 'Already refunded';
	    if (payment.refund_pending) return 'Refund pending';
	    if (payment.refund_failed) return payment.refund_failure_reason || 'Refund failed';
	    return 'Payment cannot be refunded';
	  }

	  openRefundModal(payment: any, event?: Event): void {
	    if (event) event.stopPropagation();
	    if (!this.canRefund(payment)) return;

	    this.refundPayment = payment;
	    this.selectedPayment = payment;
	    this.refundAmount = payment.amount;
	    this.refundReason = '';
	    this.refundPreview = null;
	    this.showRefundModal = true;
	    this.loadRefundPreview();
	  }

	  closeRefundModal(): void {
	    this.showRefundModal = false;
	    this.refundPayment = null;
	    this.refundAmount = 0;
	    this.refundReason = '';
	    this.refundPreview = null;
	    this.loadingPreview = false;
	  }

	  loadRefundPreview(): void {
	    if (!this.refundPayment || this.refundPayment.provider !== 'lawpay') return;
	    if (this.refundAmount <= 0 || this.refundAmount > this.refundPayment.amount) {
	      this.refundPreview = null;
	      return;
	    }

	    this.loadingPreview = true;
	    this.lawpayPaymentService.refundPreview(this.refundPayment.id, this.refundAmount)
	      .then(result => {
	        this.refundPreview = result;
	        this.loadingPreview = false;
	      })
	      .catch(error => {
	        console.error('Error loading refund preview:', error);
	        this.refundPreview = null;
	        this.loadingPreview = false;
	      });
	  }

	  confirmRefund(): void {
	    if (!this.refundPayment) return;
	    if (this.refundAmount <= 0 || this.refundAmount > this.refundPayment.amount) {
	      alert('Invalid refund amount');
	      return;
	    }

	    this.refunding = true;
	    this.onlinePaymentService.refund(
	      this.refundPayment.provider,
	      this.refundPayment.id,
	      this.refundAmount,
	      this.refundReason || 'Refund requested'
	    ).then(result => {
	      this.refunding = false;
	      if (result.success) {
	        alert(result.message || 'Refund submitted.');
	        this.closeRefundModal();
	        this.search();
	      } else {
	        alert(result.error || 'Refund failed');
	      }
	    }).catch(error => {
	      this.refunding = false;
	      console.error('Error processing refund:', error);
	      alert(this.refundErrorMessage(error));
	    });
	  }

	  refundErrorMessage(error: any): string {
	    const body = error && error.error ? error.error : error;
	    if (body && body.error) return `Refund failed: ${body.error}`;
	    if (body && body.message) return `Refund failed: ${body.message}`;
	    if (error && error.message) return `Refund failed: ${error.message}`;
	    return 'Refund failed. Please try again.';
	  }

	  trackPayment(_index: number, payment: any): string {
	    return `${payment.provider}-${payment.id}`;
  }
}
